import { shallowRef, type ShallowRef } from 'vue'
import { LanguageId } from '@previo/codebooks'
import type { RealtimeCredentials } from '@previo/realtime'
import type { DeviceType } from '@previo/design-system'
import { ACL } from '~/store/user/model/ACL'
import type { UserTypeId } from '~/codebooks/user/UserTypeId'
import type { User as UserData } from '~/store/user/http/types'
import { PmsVersionOverride } from '~/store/user/model/PmsVersionOverride'
import { CallableOnce } from '~/decorators/CallableOnce'

export class User {
  private readonly pmsVersionOverrides: ShallowRef<PmsVersionOverride[]>

  constructor(
    public readonly id: number | null,
    public readonly name: string,
    public readonly login: string,
    public readonly languageId: LanguageId | null,
    public readonly userTypeId: UserTypeId,
    public readonly acl: ACL,
    public readonly accessiblePropertyIds: number[],
    public readonly realtime: RealtimeCredentials,
    pmsVersionOverrides: PmsVersionOverride[],
  ) {
    this.pmsVersionOverrides = shallowRef(pmsVersionOverrides)
  }

  static fromResponseData(data: UserData): User {
    return new User(
      data.id,
      data.name,
      data.login,
      data.languageId,
      data.userTypeId,
      ACL.fromResponseData(data.acl),
      data.accessiblePropertyIds,
      data.realtime,
      data.pmsVersionOverrides.map((override) => PmsVersionOverride.fromResponseData(override)),
    )
  }

  isForcedToNewPmsVersion(deviceType: DeviceType): boolean {
    return !!this.findPmsVersionOverride(deviceType)
  }

  canSwitchPmsVersion(deviceType: DeviceType): boolean {
    return this.findPmsVersionOverride(deviceType)?.canSwitch ?? true
  }

  @CallableOnce()
  releasePmsVersionOverride(deviceType: DeviceType): void {
    this.pmsVersionOverrides.value = this.pmsVersionOverrides.value.filter(
      (override) => override.deviceType !== deviceType,
    )
  }

  private findPmsVersionOverride(deviceType: DeviceType): PmsVersionOverride | null {
    return (
      this.pmsVersionOverrides.value.find((override) => override.deviceType === deviceType) ?? null
    )
  }
}
